import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Mail, ArrowRight, Check, X } from 'lucide-react';
import { useAction } from 'convex/react';
import { api } from '@/lib/convex';
import { isValidEmail } from '@/lib/email';

export function Newsletter() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState<'initial' | 'success' | 'error'>('initial');
  
  const sendEmail = useAction(api.email.sendEmail);
  const subscribeToNewsletter = useAction(api.email.subscribeToNewsletter);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    if (!isValidEmail(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Send newsletter signup subscription
      const result = await subscribeToNewsletter({
        email
      });

      if (result.success) {
        setFormState('success');
        setEmail('');
        toast({
          title: "Success!",
          description: "You've been subscribed to our newsletter.",
        });
      } else {
        setFormState('error');
        toast({
          title: "Subscription failed",
          description: result.error || "An error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setFormState('error');
      toast({
        title: "Subscription failed",
        description: "An error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      // Reset form state after 3 seconds
      setTimeout(() => {
        setFormState('initial');
      }, 3000);
    }
  };

  return (
    <section className="py-16 bg-dogblue relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Join the @makershelpdesk Newsletter</h2>
          <p className="text-white/90 mb-8">
            Get no-code, marketing & SEO content tricks you won't find anywhere else.
          </p>
          
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="email"
                placeholder="Enter your email"
                className="pl-10 py-6 bg-white text-gray-800"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting || formState !== 'initial'}
              />
              {formState === 'success' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Check className="h-5 w-5 text-green-500" />
                </div>
              )}
              {formState === 'error' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <X className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            <Button 
              type="submit" 
              className="bg-black text-white hover:bg-black/80 font-semibold py-6"
              disabled={isSubmitting || formState !== 'initial'}
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
          
          <p className="text-white/70 text-sm mt-4">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </div>
      </div>
    </section>
  );
}